// src/components/EditStaffModal.tsx

import React, { useCallback, useEffect, useState } from 'react'
import debounce from 'lodash-es/debounce'
import { Button, FormPro, Input, Radio } from '@roo/roo'
import Modal, { ConfirmCallback } from '@roo/roo/Modal'
import Selector from '@roo/roo/Selector'
import TreeSelect from '@roo/roo0119/TreeSelect'
import ConfigProvider from '@roo/roo0119/ConfigProvider'
import { i18nClient, useTranslation } from '@sailor/i18n-web'
import { createStaff, updateStaff, IStaffInfo } from '../common/api'
import entInfoAtom from '@/common/recoil/atom/entInfoAtom'
import { useRequest } from 'ahooks'
import { useCostCenterOptions, useDeptTreeSelect } from '@/hooks'
import { StaffStatusEnum } from '@/pages/rule/pages/detail/utils'
import { failToast, successToast } from '@/common/utils/toast'
import SwithDatePicker from './SwithDatePicker'
import QuestionTooltip from '@/components/QuestionTooltip'
import { isEqualString } from '@/common/utils'
import WarningAlert from '@/pages/order/components/WarningAlert'
import { fetchGroupList, IGroupItem } from '../common/groupApi'
import styles from './EditStaffModal.module.less'
// departmentId 回填：TreeSelect 新版直接返回 number，或 labelInValue 模式下返回对象

interface FormProps {
  onSuccess: () => void;
  record?: IStaffInfo;
}
interface BatchModalProps extends FormProps{
  visible?: boolean;
  onClose: () => void;
}

// 外部使用一个引用接收实例,默认页面只能有一个实例
let modalIns: ConfirmCallback | null = null

const StaffForm: React.FC<FormProps> = (props) => {
  const { onSuccess, record } = props
  const { t } = useTranslation()
  const [form] = FormPro.useForm()
  const isCreate = !props.record?.staffId
  const entInfo = entInfoAtom.useData()

  const {
    treeOptions: deptTreeOptions,
    isSearching: isDeptSearching,
    init: initDeptTree,
    handleSearch: handleDeptSearch,
    handlePopupScroll: handleDeptPopupScroll,
  } = useDeptTreeSelect()

  // 弹窗打开（组件挂载）时初始化部门数据和成本中心数据
  useEffect(() => {
    initDeptTree()
    if (entInfo?.costCenterEnabled) {
      initCostCenterOptions()
    }
  }, [])

  const [groupKeyword, setGroupKeyword] = useState('')
  const { data: groupListRes, loading: groupOptionsLoading } = useRequest(
    () => fetchGroupList({ pageNo: 1, pageSize: 200, ...(groupKeyword ? { keyword: groupKeyword } : {}) }).then(res => res.data),
    { refreshDeps: [groupKeyword] }
  )
  const handleGroupSearch = useCallback(debounce((kw: string) => setGroupKeyword(kw), 300), [])

  const groupOptions: { value: number; label: string }[] = groupOptionsLoading
    ? []
    : (groupListRes?.result || []).map((g: IGroupItem) => ({ value: g.id, label: g.name }))

  // 成本中心选项（costCenterEnabled=true 时懒加载，支持搜索+分页）
  const {
    options: costCenterOptions,
    loading: costCenterOptionsLoading,
    init: initCostCenterOptions,
    handleSearch: handleCostCenterSearch,
    handlePopupScroll: handleCostCenterPopupScroll,
  } = useCostCenterOptions()

  // 编辑时回填部门、分组和成本中心
  useEffect(() => {
    if (!isCreate && record) {
      const groupIds = (record.groups || []).map(g => g.id)
      form.setFieldsValue({
        // departmentId 为 "0"/null/undefined 表示无上级部门，TreeSelect 中无对应选项，回填时置为 undefined 显示 placeholder
        departmentId: (!record.departmentId || record.departmentId === '0') ? undefined : record.departmentId,
        groupIds,
        costCenterId: record.costCenterId || undefined,
      })
    }
  }, [record, isCreate])

  const { run: handleFormSubmit, loading: submitting } = useRequest(async (formVal: IStaffInfo & { departmentId?: any; groupIds?: number[] }) => {
    const requestInstance = isCreate ? createStaff : updateStaff
    // departmentId：TreeSelect 直接返回 number，或回填时的纯 id
    // 用 null/undefined 判断，避免 id=0（无上级部门）被 || 误过滤
    const departmentId = typeof formVal.departmentId === 'object' ? formVal.departmentId?.value : formVal.departmentId

    await requestInstance({
      name: formVal.name?.trim(),
      email: isCreate ? formVal.email?.trim() : undefined, // 编辑时,不传email.因为部分企业email是加密的,后端会报错
      status: formVal.status,
      departmentId: (departmentId === null || departmentId === undefined) ? undefined : departmentId,
      groupIds: formVal.groupIds || [],
      costCenterId: formVal.costCenterId || undefined,
      staffId: record?.staffId as string,
      employeeNum: formVal?.employeeNum,
      scheduledResignTime: formVal?.scheduledResignTime || 0,
    })
    successToast(isCreate ? t('e_pc_create_success', '创建成功') : t('e_pc_update_success', '修改成功'))
    onSuccess()
    ;(modalIns as any)?.close()

  }, {  manual: true })

  const formStaffStatus = FormPro.useWatch(['status'], form)
  const isInJob = formStaffStatus === StaffStatusEnum.IN_JOB
  const isRecordInJob = isEqualString(record?.status, StaffStatusEnum.IN_JOB)

  return (
    <FormPro
      form={form}
      colon={false}
      wrapperCol={{ span: 9 }}
      labelCol={{ span: 3 }}
      initialValues={{
        ...(record || {}),
        status: record?.status ? String(record.status) : StaffStatusEnum.IN_JOB,
      }}
      onFinish={handleFormSubmit}
      onFinishFailed={(errorInfo: any) => {
        try {
          const msg = errorInfo.errorFields[0].errors[0]
          msg?.trim() && failToast(msg)
        } catch (error) {
          console.log(error)
        }
      }}
    >
      <FormPro.Item
        label={t('staff_name', '姓名')}
        name="name"
        rules={[{ required: false, message: t('e_pc_enter_name', '请输入员工姓名,最多可输入64个字符'), max: 64 }]}
      >
        <Input placeholder={t('e_pc_enter_name', '请输入员工姓名,最多可输入64个字符')}/>
      </FormPro.Item>
      <FormPro.Item
        label={t('staff_email', '邮箱')}
        validateTrigger={['onBlur']}
        name="email"
        rules={[{ required: isCreate || Boolean(record?.email), message: t('e_pc_enter_email', '请输入电子邮箱账户'), type: 'email' }]}
      >
        {
          isCreate
            ? <Input type="text" placeholder={t('e_pc_enter_email', '请输入电子邮箱账户')} readOnly={!isCreate}/>
            : record?.email
        }
      </FormPro.Item>
      {!isCreate && (
        <>
          <FormPro.Item
            name="status"
            label={t('staff_status', '状态')}
          >
            <Radio.Group disabled={isCreate}>
              <Radio value={StaffStatusEnum.IN_JOB}>{t('e_pc_employed', '在职')}</Radio>
              <Radio value={StaffStatusEnum.DIS_MISSION}>{t('e_pc_leave', '离职')}</Radio>
              {!isInJob && isRecordInJob && (<WarningAlert />)}
            </Radio.Group>
          </FormPro.Item>
        </>
      )}
      {!isCreate && isInJob && (
        <FormPro.Item needLabel style={{ margin: 0 }}>
          <FormPro.Item
            name="scheduledResignTime"
            wrapperCol={{ span: 12 }}
            labelCol={{ span: 0  }}
          >
            <SwithDatePicker
              label={<span>{t('e_pc_order_resign', '预约离职')}<QuestionTooltip style={{ transform: 'translateY(-2px)' }}>{t('e_pc_time_setting_rule')}</QuestionTooltip></span>}
              showTip={isRecordInJob && !record?.scheduledResignTime}
            />
          </FormPro.Item>
        </FormPro.Item>
      )}
      <ConfigProvider config={{ prefixCls: 'roo0119' }}>
        <FormPro.Item
          validateTrigger={['onBlur']}
          label={t('staff_department', '部门')}
          name="departmentId"
          className={styles.deptTreeSelectFormItem}
        >
          <TreeSelect
            useNew
            treeData={deptTreeOptions}
            treeDefaultExpandAll={isDeptSearching}
            placeholder={t('e_pc_select_dept_placeholder', '请选择或搜索部门')}
            showSearch={{ onSearch: handleDeptSearch }}
            onPopupScroll={handleDeptPopupScroll}
            allowClear
          />
        </FormPro.Item>
      </ConfigProvider>
      <FormPro.Item
        label={t('e_pc_group_common', '分组')}
        validateTrigger={['onBlur']}
        name="groupIds"
      >
        <Selector
          multiple
          clearable
          filterable
          filterMethod={() => true}
          placeholder={t('e_pc_select_group_placeholder', '请选择或搜索分组')}
          options={groupOptions}
          onSearch={handleGroupSearch}
        />
      </FormPro.Item>
      <FormPro.Item
        validateTrigger={['onBlur']}
        label={t('e_pc_staff_id', '工号')}
        name="employeeNum"
      >
        <Input placeholder={t('e_pc_input_employee_id', '请输入工号')} />
      </FormPro.Item>
      {entInfo?.costCenterEnabled && (
        <FormPro.Item
          label={t('e_pc_cost_center_tab', '成本中心')}
          validateTrigger={['onBlur']}
          name="costCenterId"
        >
          <Selector
            clearable
            filterable
            filterMethod={() => true}
            placeholder={t('e_pc_cost_center_input', '请选择或搜索成本中心')}
            options={costCenterOptions}
            loading={costCenterOptionsLoading}
            onSearch={handleCostCenterSearch}
            onPopupScroll={handleCostCenterPopupScroll}
          />
        </FormPro.Item>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px' }}>
        <Button type="brand" onClick={form.submit} loading={submitting}>
          {t('staff_submit', '提交')}
        </Button>
      </div>
    </FormPro>
  )
}


const StaffModal = (props: BatchModalProps) => {
  const t = i18nClient.getText
  const isCreate = !props.record?.staffId
  const { visible = false, onClose, ...formProps } = props
  return (
    <Modal
      {...{
        title: isCreate ? t('e_pc_create_staff', '创建员工') as string : t('e_pc_create_edit', '修改员工') as string,
        cancelText: t('e_pc_cancel_button', 'Cancel'),
        confirmText: t('e_pc_confirm_button', 'Confirm'),
        showCancelButton: false,
        showConfirmButton: false,
        closeMotion: false,
        visible: visible,
        onCancel: onClose,
        style: { width: '600px' } }}
    >
      <StaffForm {...formProps} />
    </Modal>
  )
}

export default StaffModal
